import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Verkaufsautomat mieten“ (/verkaufsautomat-mieten).
 *
 * Ziel-Keywords: „verkaufsautomat mieten“ + „verkaufsautomaten mieten“ (~300 zus.)
 * + „automat mieten“ (70) + „verkaufsautomat leasing“ (10, CPC 6,10 €). Reiner
 * Käufer-/Entscheidungs-Intent (Miete vs. Kauf). dga-vending rankt hier #1 mit
 * einer Leasing-Seite – marktbox vermietet real (ab 300 €/Monat).
 *
 * Abgrenzung zum Kauf-Hub /verkaufsautomat: hier steht die MIETE im Fokus, inkl.
 * Miete-vs-Kauf-Vergleich als Herzstück (kein Doorway-Klon). Informationaler
 * Zubringer: Blog „ein-eigener-verkaufsautomat-kaufen-oder-mieten“.
 */
@Component({
  selector: 'app-verkaufsautomat-mieten-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './verkaufsautomat-mieten-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class VerkaufsautomatMietenPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'ein-eigener-verkaufsautomat-kaufen-oder-mieten';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/obsthof-zink/U-MB-00022'
  );

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@miete.faq1.q:Was kostet es, einen Verkaufsautomaten zu mieten?`,
      a: $localize`:@@miete.faq1.a:Die marktbox gibt es zur Miete ab 300 € pro Monat – je nach Modell (24 oder 39 Fächer) und Zubehör. So startest du ohne hohe Anfangsinvestition mit planbaren monatlichen Kosten.`,
    },
    {
      q: $localize`:@@miete.faq2.q:Verkaufsautomat mieten oder kaufen – was lohnt sich?`,
      a: $localize`:@@miete.faq2.a:Mieten senkt die Einstiegshürde und hält die Kosten planbar – ideal zum Starten, Testen oder für saisonale Standorte. Kaufen (ab 6.800 €) lohnt sich, wenn der Automat dauerhaft an einem Standort läuft. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@miete.faq3.q:Was ist bei der Miete inklusive?`,
      a: $localize`:@@miete.faq3.a:Du erhältst eine einsatzbereite marktbox mit bargeldloser Zahlung, App-Steuerung und Live-Link. Die genauen Konditionen und verfügbaren Modelle stellst du dir im Shop zusammen.`,
    },
    {
      q: $localize`:@@miete.faq4.q:Für wen eignet sich das Mieten besonders?`,
      a: $localize`:@@miete.faq4.a:Für alle, die klein und ohne großes Kapital starten wollen: Direktvermarkter am Anfang, saisonale Verkäufe oder Standorte, die erst getestet werden sollen. Später lässt sich jederzeit erweitern.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@miete.metaTitle:Verkaufsautomat mieten – ab 300 €/Monat | marktbox`;
    const description =
      $localize`:@@miete.metaDesc:Verkaufsautomat mieten statt kaufen: mit der marktbox ab 300 €/Monat starten – ohne hohe Anfangsinvestition, flexibel und planbar.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@miete.metaKeywords:Verkaufsautomat mieten, Verkaufsautomaten mieten, Automat mieten, Verkaufsautomat leasing, Verkaufsautomat mieten oder kaufen, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('miete-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox – Verkaufsautomat zur Miete',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('miete-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/verkaufsautomat-mieten`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
